/**
 * The workspace's services: `GET /api/v1/practitioner-services/` for the list
 * and `POST` to create one, `GET`/`PATCH` on `…/{public_id}/` for a single one,
 * and `…/activate/` and `…/deactivate/` for whether it is listed.
 *
 * Every one of them answers with the same record, so one mapping serves them
 * all, and the card the tab shows is built from the response rather than from
 * what was sent: the backend sets the id, the timestamps and `is_active`, and
 * on a curated service it sets the name too — from the goal, which is why
 * what comes back is the only thing worth reading.
 *
 * Not to be confused with `plansService`, which calls `/api/v1/packages/`.
 * The backend's two words are the reverse of this app's — see the note over
 * `API_ENDPOINTS.services`.
 *
 * The list is a bare array here, not DRF's envelope, so there is no page to
 * walk: a practice offers a handful of services, and the endpoint sends all
 * of them.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { formatCreatedOn } from "../planFormat";
import type {
  BrandService,
  NewBrandService,
  ServiceGoal,
  ServiceRequest,
  ServiceResponse,
} from "../types/brand.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * A goal missing its `id` or its `name` is dropped rather than carried
 * half-formed: a service is curated or it is custom, and one holding a goal
 * that cannot be named is neither.
 */
const toGoal = (goal: ServiceGoal | null | undefined): ServiceGoal | null =>
  goal && typeof goal.id === "number" && goal.name
    ? { id: goal.id, name: goal.name }
    : null;

/** The created date as the card reads it, or "" if there is nothing to read. */
const toCreatedOn = (createdAt: string | undefined): string => {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  // An unparseable date would otherwise be written into the card as the words
  // "Invalid Date", which is worse than the blank the card already handles.
  return Number.isNaN(date.getTime()) ? "" : formatCreatedOn(date);
};

/**
 * The card the tab lists, out of the record any of the endpoints answers with.
 *
 * `current` is the card already on screen, passed by everything that changes
 * one rather than reads it fresh. The edit and the two listing calls answer
 * with a record that need not be complete — the `PATCH` has been seen to come
 * back without the timestamps — and a card rebuilt from that alone would lose
 * its created date to a rename. So a field the response does not carry keeps
 * the value it had.
 *
 * Only an absent field falls back. A `goal` of `null` is an answer — a custom
 * service — and is taken as one; it is `undefined` that means the response
 * simply did not say.
 */
const toBrandService = (
  response: ServiceResponse,
  current?: BrandService
): BrandService => ({
  id: response.public_id ?? current?.id ?? "",
  name: response.name ?? current?.name ?? "",
  // The backend sends "" for a service written without one, so this fallback
  // is only for a response that left the key out altogether.
  description: response.description ?? current?.description ?? "",
  goal: response.goal === undefined ? current?.goal ?? null : toGoal(response.goal),
  createdOn: toCreatedOn(response.created_at) || current?.createdOn || "",
  status: response.is_active ? "Active" : "In-active",
});

/**
 * Every service the workspace has, listed or not.
 *
 * The endpoint takes `?is_active=true`, and this deliberately does not send it.
 * The tab is where a service is taken down and put back up, and a filtered
 * list is one a deactivated service drops out of — leaving no way to reach it
 * again. The card wears an In-active pill for exactly this, the way a plan's
 * row does.
 *
 * The filter is what the *public* page wants, which reads this data through
 * its own endpoint rather than through here.
 */
export async function fetchServices(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<BrandService[]> {
  const response = await api.get<ServiceResponse[]>(
    API_ENDPOINTS.services.list,
    { token: await token(getToken, "load your services"), signal }
  );

  // A bare array, but guarded: a body that is anything else is an empty list
  // rather than a `.map` of undefined halfway up the tab.
  // Mapped through an arrow rather than passed by reference: `map` would hand
  // the index in as the second argument, which this one reads as a card.
  return Array.isArray(response)
    ? response.map((service) => toBrandService(service))
    : [];
}

/**
 * One service, by the `public_id` a listed card carries.
 *
 * Nothing on the Services tab calls this yet: the list already carries every
 * field a card draws, so re-reading one before opening it would be a request
 * for something in hand. It is what a detail screen, or a dialog wanting to be
 * sure it was editing the current version, would open with — the same place
 * `fetchPlan` sits in `plansService`.
 */
export async function fetchService(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<BrandService> {
  const response = await api.get<ServiceResponse>(
    API_ENDPOINTS.services.detail(publicId),
    { token: await token(getToken, "load this service"), signal }
  );

  return toBrandService(response);
}

/**
 * Creates a service and returns the card the backend stored.
 *
 * What goes up depends on where the name came from. Picked from the goals, it
 * posts the id and lets the backend name the service; typed, it posts the
 * text and the service has no goal behind it. The one case in between — a goal
 * picked and then renamed — posts both, which is why the name is compared
 * rather than always sent: `{ goal_id, name }` where the name is the goal's
 * own would record a rename that nobody made.
 */
export async function createService(
  getToken: GetToken,
  draft: NewBrandService,
  signal?: AbortSignal
): Promise<BrandService> {
  const body: ServiceRequest = {
    ...(draft.goal ? { goal_id: draft.goal.id } : {}),
    // Always on a custom service — the typed text is the only name it has —
    // and on a curated one only where the name was edited away from the
    // goal's. Sending a name that is already the goal's own would record a
    // rename nobody made.
    ...(draft.goal && draft.name === draft.goal.name
      ? {}
      : { name: draft.name }),
    description: draft.description,
  };

  const response = await api.post<ServiceResponse>(
    API_ENDPOINTS.services.list,
    body,
    { token: await token(getToken, "add a service"), signal }
  );

  return toBrandService(response);
}

/**
 * What an edit would change, which is all a `PATCH` sends. Empty where the
 * dialog was opened and closed again without anything being touched.
 *
 * Only the two fields the endpoint takes. A curated service's goal is not
 * among them — it is fixed at creation — so a rename leaves the record
 * curated, still pointing at the goal it was made from, and only the words on
 * the card change.
 */
const changesBetween = (
  service: BrandService,
  draft: NewBrandService
): ServiceRequest => {
  const changes: ServiceRequest = {};

  if (draft.name !== service.name) changes.name = draft.name;
  // A description cleared back to blank is a change like any other, and goes
  // up as the empty string — which is what the backend stores for "none".
  if (draft.description !== service.description) {
    changes.description = draft.description;
  }

  return changes;
};

/**
 * Saves an edit, and returns the card as the backend now holds it.
 *
 * A dialog that was opened and closed with nothing altered sends nothing and
 * answers with the service unchanged: an empty `PATCH` is a request that can
 * only fail or do nothing, and either way it is one the user did not ask for.
 */
export async function updateService(
  getToken: GetToken,
  service: BrandService,
  draft: NewBrandService,
  signal?: AbortSignal
): Promise<BrandService> {
  const changes = changesBetween(service, draft);
  if (Object.keys(changes).length === 0) return service;

  const response = await api.patch<ServiceResponse>(
    API_ENDPOINTS.services.detail(service.id),
    changes,
    { token: await token(getToken, "save this service"), signal }
  );

  // Merged over the service in hand: this response carries no timestamps, and
  // the card would otherwise lose its created date to a rename.
  return toBrandService(response, service);
}

/**
 * Lists a service on the public page, or takes it off again.
 *
 * Its own pair of endpoints rather than a field on the `PATCH`, so this sends
 * no body at all — the path is the whole of the request. The card is rebuilt
 * from the response, which means the pill follows the backend's `is_active`
 * rather than the button that was pressed.
 */
export async function setServiceActive(
  getToken: GetToken,
  service: BrandService,
  active: boolean,
  signal?: AbortSignal
): Promise<BrandService> {
  const path = active
    ? API_ENDPOINTS.services.activate(service.id)
    : API_ENDPOINTS.services.deactivate(service.id);

  const response = await api.post<ServiceResponse>(path, undefined, {
    token: await token(
      getToken,
      active ? "activate this service" : "deactivate this service"
    ),
    signal,
  });

  return toBrandService(response, service);
}
